import React from "react";
import { Check } from "lucide-react";
import useClipboard from "../../../hooks/useClipboard";
import { useNotificationsContext } from "../../../hooks/useNotificationsContext";
import { WebViewTooltip } from "../../Pages/Diary/SubHeader/WebViewTooltip";
import { cn } from "../../../lib/utils";

interface CopyableBookingRefProps {
  reference: string | number | null | undefined;
  className?: string;
  prefix?: string;
}

export const CopyableBookingRef: React.FC<CopyableBookingRefProps> = ({
  reference,
  className,
  prefix = "DMN-"
}) => {
  const clipboard = useClipboard();
  const { addNotification } = useNotificationsContext();

  if (!reference) {
    return null;
  }

  const fullReference = `${prefix}${reference}`;

  const copyToClipboard = async () => {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }

    const success = await clipboard.copy(fullReference);
    if (success) {
      addNotification({
        heading: "Copied!",
        autoClose: 2000,
        toastType: "success"
      });
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    copyToClipboard();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      copyToClipboard();
    }
  };

  return (
    <WebViewTooltip
      content={clipboard.copied ? "Copied!" : "Click to copy"}
      side="top"
    >
      <button
        type="button"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          "bg-transparent border-none p-0 cursor-text",
          "text-[var(--fontSize-m)] font-medium text-foreground whitespace-nowrap",
          "hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm",
          "inline-flex items-center gap-1",
          className
        )}
        style={{ userSelect: "text", WebkitUserSelect: "text" }}
        aria-label={
          clipboard.copied
            ? `Copied ${fullReference}`
            : `Copy ${fullReference} to clipboard`
        }
      >
        {fullReference}
        {clipboard.copied && (
          <Check className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
        )}
      </button>
    </WebViewTooltip>
  );
};
